import React, { useEffect, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AdBanner from "@/components/feature/AdBanner";
import { useLiveMatches, useMatches } from "@/hooks/useSupabaseData";
import { useAnalytics } from "@/hooks/useAnalytics";
import { supabase } from "@/lib/supabase";

type Transmissao = {
  id: number;
  url: string;
  titulo: string | null;
  descricao: string | null;
  scheduled_start_at: string | null;
  scheduled_end_at: string | null;
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string | null;
  ativo: boolean | null;
};

function formatScheduleDate(value: string) {
  return new Date(value).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AoVivoPage() {
  const { matches: liveMatches } = useLiveMatches();
  const { matches: allMatches } = useMatches();

  useAnalytics("/ao-vivo");

  const [transmissao, setTransmissao] = useState<Transmissao | null>(null);
  const [currentTime, setCurrentTime] = useState(() => Date.now());

  const nextMatch = allMatches.find((m) => m.status === "programado");
  const activeMatch = liveMatches[0] || nextMatch;
  const isLive = activeMatch?.status === "ao_vivo";

  useEffect(() => {
    async function carregarTransmissao() {
      const { data } = await supabase
        .from("transmissoes")
        .select(`
          id,
          url,
          titulo,
          descricao,
          scheduled_start_at,
          scheduled_end_at,
          seo_title,
          seo_description,
          seo_keywords,
          ativo
        `)
        .eq("id", 1)
        .single();

      if (data) {
        setTransmissao(data);
      }
    }

    carregarTransmissao();
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => setCurrentTime(Date.now()), 30000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!transmissao) return;

    document.title =
      transmissao.seo_title ||
      transmissao.titulo ||
      "Ao Vivo | Central Alvinegra";

    const updateMeta = (name: string, content?: string | null) => {
      if (!content) return;

      let meta = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement | null;

      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", name);
        document.head.appendChild(meta);
      }

      meta.setAttribute("content", content);
    };

    updateMeta("description", transmissao.seo_description);
    updateMeta("keywords", transmissao.seo_keywords);
  }, [transmissao]);

  const startsAt = transmissao?.scheduled_start_at
    ? new Date(transmissao.scheduled_start_at).getTime()
    : null;
  const endsAt = transmissao?.scheduled_end_at
    ? new Date(transmissao.scheduled_end_at).getTime()
    : null;
  const isBeforeSchedule = Boolean(startsAt && currentTime < startsAt);
  const isAfterSchedule = Boolean(endsAt && currentTime > endsAt);
  const canShowTransmission = Boolean(transmissao?.ativo !== false && transmissao?.url && !isBeforeSchedule && !isAfterSchedule);
  const scheduleMessage = isBeforeSchedule && transmissao?.scheduled_start_at
    ? `Transmissão agendada para ${formatScheduleDate(transmissao.scheduled_start_at)}.`
    : isAfterSchedule
      ? "Transmissão encerrada."
      : "Nenhuma transmissão configurada.";

  return (
    <div className="min-h-screen bg-white relative">
      <Navbar />

      <div className="absolute top-0 left-0 right-0 h-48 md:h-64 bg-gradient-to-b from-alvinegro-black to-white z-0" />

      <main className="pt-20 md:pt-24 pb-12 relative z-10">
        <div className="w-full px-4 md:px-8">
          <Breadcrumb
            items={[
              { label: "Início", path: "/" },
              { label: "Ao Vivo" },
            ]}
          />

          <div className="mb-8">
            <h1 className="font-serif font-bold text-3xl md:text-4xl text-alvinegro-black mb-4">
              {isLive ? (
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  Ao Vivo Agora
                </span>
              ) : (
                "Ao Vivo"
              )}
            </h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2">
              <div className="bg-alvinegro-black rounded-2xl overflow-hidden mb-6">
                <div className="relative aspect-video">
                  {canShowTransmission ? (
                    <iframe
                      src={transmissao!.url}
                      className="w-full h-full border-0"
                      allowFullScreen
                      allow="autoplay; encrypted-media; picture-in-picture"
                      title="Transmissão Ao Vivo"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-black flex items-center justify-center">
                      <p className="text-white text-center px-6">
                        {scheduleMessage}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6">
                <AdBanner
                  variant="leaderboard"
                  label="Publicidade"
                />
              </div>
            </div>

            <div>
              <div className="bg-white rounded-2xl border border-alvinegro-border p-6">
                <h2 className="text-xl font-bold text-alvinegro-black mb-4">
                  {transmissao?.titulo || "Transmissão Ao Vivo"}
                </h2>

                <div className="prose max-w-none">
                  <p className="text-alvinegro-dark leading-relaxed whitespace-pre-wrap">
                    {transmissao?.descricao ||
                      "Descrição não cadastrada."}
                  </p>
                </div>

                {(transmissao?.scheduled_start_at || transmissao?.scheduled_end_at) && (
                  <div className="mt-5 rounded-xl bg-alvinegro-light p-4 text-sm text-alvinegro-dark">
                    {transmissao?.scheduled_start_at && (
                      <p>
                        <strong>Início:</strong> {formatScheduleDate(transmissao.scheduled_start_at)}
                      </p>
                    )}
                    {transmissao?.scheduled_end_at && (
                      <p className="mt-1">
                        <strong>Fim:</strong> {formatScheduleDate(transmissao.scheduled_end_at)}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-6 bg-alvinegro-light rounded-2xl p-6">
                <h3 className="font-semibold text-sm text-alvinegro-black mb-4">
                  Onde Assistir
                </h3>

                <div className="space-y-2">
                  {[
                    {
                      name: "Premiere",
                      type: "TV por assinatura",
                    },
                    {
                      name: "SporTV",
                      type: "TV por assinatura",
                    },
                    {
                      name: "Globoplay",
                      type: "Streaming",
                    },
                    {
                      name: "Cazé TV",
                      type: "YouTube",
                    },
                  ].map((channel) => (
                    <div
                      key={channel.name}
                      className="flex items-center justify-between p-3 bg-white rounded-xl"
                    >
                      <div>
                        <p className="text-sm font-medium text-alvinegro-black">
                          {channel.name}
                        </p>

                        <p className="text-xs text-alvinegro-muted">
                          {channel.type}
                        </p>
                      </div>

                      <i className="ri-tv-line text-alvinegro-muted" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <AdBanner
                  variant="medium-rectangle"
                  label="Publicidade"
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
